import { DebugPanelController } from './controllers/debugPanelController.js';
import { wireDebugStructureDestroy } from './controllers/debugStructureWire.js';
import { wireMatchOutcome } from './controllers/matchOutcomeWire.js';
import { wireMatchEndUi } from './controllers/matchEndUiWire.js';
import { wireAramRegressionHook } from './controllers/wireAramRegressionHook.js';
import { wireVisionDebug } from './controllers/wireVisionDebug.js';
import { wireMpStubDebug } from './controllers/wireMpStubDebug.js';
import { attachBotLaneMoveHook } from './controllers/wireBotLaneMoveDebug.js';
import { attachCastTimingHook } from './controllers/wireCastTimingDebug.js';
import { attachCombatDebugHooks } from './controllers/wireCombatDebug.js';
import { attachShopZoneDebugHooks } from './controllers/wireShopZoneDebug.js';
import { wireDebugPanelVisibility } from './controllers/debugPanelVisibility.js';
import { wireMatchTelemetryDebug } from './controllers/wireMatchTelemetryDebug.js';
import { wireDebugCamera } from './controllers/wireDebugCamera.js';

/**
 * bootDebugTools — Debug panel + structure destroy + match end UI hooks.
 */
export function bootDebugTools(deps) {
  const {
    championEntity,
    garenEntity,
    dummyEntity,
    blueSpawner,
    redSpawner,
    floatingTextUI,
    shopUI,
    spawnJungleCamps,
    getActiveJungleCamps,
    switchChampion,
    matchRuntime,
    inputController
  } = deps;

  const debugPanelVisibility = wireDebugPanelVisibility(document);

  const debugPanelController = new DebugPanelController({
    championEntity,
    garenEntity,
    dummyEntity,
    blueSpawner,
    redSpawner,
    floatingTextUI,
    shopUI,
    spawnJungleCampsCallback: () => spawnJungleCamps(),
    getActiveJungleCampsCallback: () => getActiveJungleCamps(),
    switchChampionCallback: switchChampion
  });
  debugPanelController.init();

  wireDebugStructureDestroy({
    getStructures: () => matchRuntime.entities.getStructures(),
    getChampion: () => championEntity,
    floatingTextUI
  });

  const matchTelemetry = wireMatchTelemetryDebug();
  matchTelemetry.start();

  wireDebugCamera({
    globalObj: typeof window !== 'undefined' ? window : globalThis,
    getChampion: () => championEntity || { x: 0, z: 0 }
  });

  const matchEndUi = wireMatchEndUi({
    getPlayerTeam: () => championEntity?.team || 'blue',
    matchRuntime,
    inputController,
    matchTelemetry
  });

  const matchOutcome = wireMatchOutcome({
    matchRuntime,
    onEnded: matchEndUi.onEnded
  });

  wireAramRegressionHook();
  wireVisionDebug({
    matchRuntime,
    getPlayer: () => championEntity,
    getWallBoxes: () => deps.wallBoxes || [],
    getBushZones: () => deps.bushZones || [],
    getMapSceneState: () => deps.getMapSceneState?.() || null,
    getAlliedViewers: () => deps.getAlliedViewers?.() || []
  });
  wireMpStubDebug();

  if (typeof window !== 'undefined') {
    attachBotLaneMoveHook(window);
    attachCastTimingHook(window);
    attachCombatDebugHooks(window);
    attachShopZoneDebugHooks(window);
    window.__debugGetAramStartStats = () => ({
      mapId: matchRuntime?.mapId || null,
      level: championEntity?.level ?? null,
      gold: championEntity?.gold ?? null,
      outgoingHealingMultiplier: championEntity?.outgoingHealingMultiplier ?? null
    });
    window.__debugGetGoldEconomy = () => ({
      mapId: matchRuntime?.mapId || null,
      passiveGoldRate: matchRuntime?.lastPlan?.passiveGoldRate ?? null,
      passiveXpRate: matchRuntime?.lastPlan?.passiveXpRate ?? null,
      gold: championEntity?.gold ?? null,
      exp: championEntity?.exp ?? null,
      level: championEntity?.level ?? null
    });
  }

  return {
    debugPanelController,
    matchOutcome,
    matchEndUi,
    debugPanelVisibility,
    matchTelemetry
  };
}
